import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    AnimatedActionBarModeTransition,
    BackButton,
    EventCard,
    EventsExportSelectionBar,
} from '@/components';
import { EventsListLayout } from '@/features/EventsListLayout';
import { EventsBrowseBar, EventFilter, EventSort } from '@/features/BrowseBar';
import type { EventsBrowseBarConfig } from '@/features/BrowseBar';
import { usePaginatedAlerts } from '@/features/EventsListLayout/hooks';
import { EventFilterView } from '@/features/EventFilterView';
import { SidePanel } from '@/components/SidePanel';
import { EngineService } from '@/services/engine';
import { useQueryFiltersStore, useSourcesStore } from '@/stores';
import {
    usePipEventWatcher,
    usePipPlayerStore,
} from '@/stores/pip-player.store';
import {
    useAddEventToReportMenuItem,
    useAddEventsToReportAction,
    useDeletedItems,
    useDocumentTitle,
    useEventsExport,
    useExportEventMenuItem,
    useGridMultiSelection,
    useOpenInNewWindow,
} from '@/hooks';
import { hasRouterHistory } from '@/stores/navigation-history.store';
import { SortOption } from '@/utils/query-filters';
import { getAlertDescription } from '@/utils/detection-classes';
import { buildEventCardMetadata } from '@/utils/event-card-metadata';
import { getEventTimeRange } from '@/utils/event-time-range';
import { isVideoRangeEvent } from '@/utils/date';

const ALERTS_CONFIG: EventsBrowseBarConfig = {
    filters: [
        EventFilter.CONFIDENCE,
        EventFilter.DATE_RANGE,
        EventFilter.SOURCE,
        EventFilter.ALERT_RULE,
    ],
    sorts: [EventSort.DATE, EventSort.CONFIDENCE],
};

export default function AlertsPage() {
    useDocumentTitle('alerts');
    const { t } = useTranslation([
        I18nNamespace.COMMON,
        I18nNamespace.EVENTS,
        I18nNamespace.SOURCES,
    ]);
    const [searchParams] = useSearchParams();
    const { id: alertRuleId } = useParams<{ id: string }>();
    const { setAlertRuleSelection, clearAlertRuleSelection } =
        useQueryFiltersStore();
    const { getSourceById } = useSourcesStore();
    const pipOpen = usePipPlayerStore((s) => s.open);
    const isPipPlaying = usePipEventWatcher();
    const addToReportItem = useAddEventToReportMenuItem();
    const exportEventItem = useExportEventMenuItem();
    const selection = useGridMultiSelection();
    const alertsExport = useEventsExport({
        onStarted: selection.exitMultiSelectMode,
    });
    const refreshPipNavigationContext = usePipPlayerStore(
        (s) => s.refreshNavigationContext,
    );

    const queryAlertRuleId = searchParams.get('alertRuleId') || undefined;
    const { openEvent } = useOpenInNewWindow();

    const effectiveAlertRuleId = alertRuleId || queryAlertRuleId;

    useEffect(() => {
        if (effectiveAlertRuleId) {
            setAlertRuleSelection(new Set([effectiveAlertRuleId]));
            return;
        }
        clearAlertRuleSelection();
    }, [effectiveAlertRuleId, setAlertRuleSelection, clearAlertRuleSelection]);

    const {
        events,
        totalItems,
        page,
        pageSize,
        onPageChange,
        error,
        isLoading,
        isFetching,
        pageStateKey,
    } = usePaginatedAlerts({
        alertRuleId: effectiveAlertRuleId,
        pageKey: 'alerts',
    });

    const onEventClick = useCallback(
        (eventId: string, event?: MouseEvent) => {
            openEvent(eventId, event, {
                state: {
                    from: 'alerts',
                    feedContext: effectiveAlertRuleId
                        ? {
                              type: 'alertRule',
                              alertRuleId: effectiveAlertRuleId,
                          }
                        : { type: 'alerts' },
                },
            });
        },
        [openEvent, effectiveAlertRuleId],
    );

    const { filteredItems: filteredEvents, deletedCount } = useDeletedItems({
        items: events,
        getItemId: (item) => item.event.id,
        deleteItemsFn: (ids) => EngineService.deleteEvents(ids),
        successMessage: t('actions.deleteSuccess'),
        errorMessage: t('actions.deleteError'),
    });
    const addToReportAction = useAddEventsToReportAction({
        events: filteredEvents.map((alert) => ({
            event: alert.event,
            confidence: alert.confidence,
        })),
        selection,
    });
    const { clearSelection: clearEventSelection } = selection;
    useEffect(() => {
        clearEventSelection();
    }, [page, clearEventSelection]);

    useEffect(() => {
        refreshPipNavigationContext({
            type: 'events-page',
            items: filteredEvents.map((a) => ({ id: a.event.id })),
            canLoadMore: false,
        });
    }, [filteredEvents, refreshPipNavigationContext]);

    const uniqueSourceCount = useMemo(() => {
        if (totalItems > pageSize) return undefined;
        return new Set(filteredEvents.map((item) => item.event.sourceId)).size;
    }, [filteredEvents, totalItems, pageSize]);

    if (error) {
        return (
            <div className="h-full flex items-center justify-center">
                <p className="text-red-400">{t('events:page.errorLoading')}</p>
            </div>
        );
    }

    const showBackButton = hasRouterHistory();
    const visibleEventIds = Array.from(
        new Set(filteredEvents.map((alert) => alert.event.id)),
    );
    const alertCount = totalItems
        ? totalItems - deletedCount
        : filteredEvents.length;

    return (
        <div className="relative h-full min-h-0">
            <SidePanel side="left">
                <EventFilterView config={ALERTS_CONFIG} />
            </SidePanel>
            {showBackButton && <BackButton variant="floating" />}
            <EventsListLayout
                pageKey={pageStateKey ?? 'alerts'}
                eventCount={alertCount}
                isEmpty={filteredEvents.length === 0}
                sourceCount={uniqueSourceCount}
                query={null}
                isLoading={isLoading}
                pagination={{
                    mode: 'paginated',
                    page,
                    pageSize,
                    totalCount: totalItems,
                    isFetching,
                    onPageChange,
                }}
                actionBar={
                    <AnimatedActionBarModeTransition
                        isMultiSelectMode={selection.isMultiSelectMode}
                        browseSlot={
                            <EventsBrowseBar
                                config={ALERTS_CONFIG}
                                sourcesMenuMode="cameras"
                                initialSortOption={SortOption.DATE_NEWEST}
                                onEnterMultiSelect={
                                    selection.enterMultiSelectMode
                                }
                            />
                        }
                        selectionSlot={
                            <EventsExportSelectionBar
                                selection={selection}
                                visibleEventIds={visibleEventIds}
                                totalCount={visibleEventIds.length}
                                entityLabel={t('pageTitle.alerts')}
                                exportBusy={alertsExport.busy}
                                onExport={alertsExport.startExport}
                                bulkActions={[addToReportAction]}
                            />
                        }
                    />
                }
                emptyMessage={t('events:page.noAlerts')}
                displayMode="alerts"
            >
                {filteredEvents.map((alert, index) => {
                    const event = alert.event;
                    const source = getSourceById(event.sourceId);
                    const isWatchingThis = isPipPlaying(event.id);
                    const sourceType = event.sourceType ?? source?.type;
                    const {
                        isScene,
                        startTime: eventStartTime,
                        endTime: eventEndTime,
                    } = getEventTimeRange(event);
                    const isVideoRange = isVideoRangeEvent(
                        eventStartTime,
                        eventEndTime,
                        sourceType,
                    );
                    const sourceLabel =
                        alert.sourceName ??
                        event.sourceName ??
                        source?.displayName ??
                        '';
                    const metadata = buildEventCardMetadata({
                        createdAt: event.createdAt,
                        eventStartTime,
                        eventEndTime,
                        isVideoRange,
                        sourceType,
                        sourceLabel,
                        locationName: source?.location?.name,
                    });
                    const title = isScene
                        ? alert.promptText
                        : getAlertDescription(
                              t,
                              alert.promptText,
                              event.classId,
                          );
                    return (
                        <EventCard
                            key={`${alert.alertRuleId}-${event.id}`}
                            id={event.id}
                            title={title}
                            thumbnailUrls={[
                                event.thumbnailUrl ||
                                    (isScene
                                        ? EngineService.getSceneThumbnailUrl(
                                              event.id,
                                          )
                                        : EngineService.getEventThumbnailUrl(
                                              event.id,
                                              true,
                                          )),
                            ]}
                            confidence={alert.confidence}
                            isScene={isScene}
                            metadata={metadata}
                            quickView={{
                                state: isWatchingThis ? 'watching' : 'idle',
                                onClick: () =>
                                    pipOpen({
                                        eventId: event.id,
                                        navigationContext: {
                                            type: 'events-page',
                                            items: filteredEvents.map((a) => ({
                                                id: a.event.id,
                                            })),
                                            currentIndex: index,
                                            canLoadMore: false,
                                        },
                                    }),
                            }}
                            onClick={onEventClick}
                            extraActions={[
                                ...addToReportItem(event, {
                                    confidence: alert.confidence,
                                }),
                                ...exportEventItem(event, alertsExport),
                            ]}
                            selectable={selection.isMultiSelectMode}
                            selected={selection.selectedIds.has(event.id)}
                            onSelectToggle={selection.toggleSelected}
                        />
                    );
                })}
            </EventsListLayout>
        </div>
    );
}
